import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { languages, validLangs, getCategoryName } from '@/mocks/languages';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = lang && validLangs.includes(lang) ? lang : 'en';

  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const switchLang = (code: string) => {
    setLangOpen(false);
    setMobileOpen(false);
    const parts = location.pathname.split('/').filter(Boolean);
    const slug = parts.length > 1 ? parts[1] : null;
    navigate(slug ? `/${code}/${slug}` : `/${code}`);
  };

  const navItems = [
    { key: 'categories', label: t('common.nav.categories'), href: '#categories' },
    { key: 'guides', label: t('common.nav.guides'), href: '#guides' },
    { key: 'articles', label: t('common.nav.articles'), href: '#articles' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b-2 border-background-200">
      <nav className="flex items-center justify-between gap-4 px-4 md:px-6 h-16">
        <Link to={`/${currentLang}`} className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg"></i>
          </span>
          <span className="font-heading font-semibold text-foreground-950 whitespace-nowrap text-sm md:text-base">
            AllChinaBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to={`/${currentLang}`}
            className="text-sm font-medium text-foreground-900 hover:text-primary-600 whitespace-nowrap transition-colors"
          >
            {t('common.nav.home')}
          </Link>
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="text-sm font-medium text-foreground-900 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {item.label}
            </a>
          ))}
          <Link
            to={`/${currentLang}/allchinabuy-spreadsheet`}
            className="text-sm font-medium text-foreground-900 hover:text-primary-600 whitespace-nowrap transition-colors"
          >
            {t('common.nav.shoppingGuide')}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-300 text-sm font-medium text-foreground-900 hover:bg-background-100 whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-global-line"></i>
              <span>{currentLang.toUpperCase()}</span>
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg bg-white border border-background-200 py-1">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => switchLang(l.code)}
                    className="w-full flex items-center justify-between px-4 py-2 text-sm text-foreground-900 hover:bg-background-100 cursor-pointer"
                  >
                    <span>{l.full}</span>
                    {l.code === currentLang && <i className="ri-check-line text-primary-500"></i>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            {t('common.nav.cta')}
          </a>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 text-foreground-900 cursor-pointer"
          >
            <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden px-4 pb-5 flex flex-col gap-1 bg-white border-b border-background-200 max-h-[75vh] overflow-y-auto">
          <Link
            to={`/${currentLang}`}
            onClick={() => setMobileOpen(false)}
            className="py-2.5 text-sm font-medium text-foreground-900 hover:text-primary-600"
          >
            {t('common.nav.home')}
          </Link>
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="py-2.5 text-sm font-medium text-foreground-900 hover:text-primary-600"
            >
              {item.label}
            </a>
          ))}
          <Link
            to={`/${currentLang}/allchinabuy-spreadsheet`}
            onClick={() => setMobileOpen(false)}
            className="py-2.5 text-sm font-medium text-foreground-900 hover:text-primary-600"
          >
            {t('common.nav.shoppingGuide')}
          </Link>

          <div className="mt-3 pt-3 border-t border-background-200">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500 mb-2">
              {t('common.nav.categories')}
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-foreground-800 hover:bg-background-100 whitespace-nowrap"
                >
                  <i className={`${c.icon} text-foreground-500`}></i>
                  <span>{getCategoryName(c, currentLang)}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="mt-3 inline-flex justify-center px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold"
          >
            {t('common.nav.cta')}
          </a>
        </div>
      )}
    </header>
  );
}